import { useLayoutEffect } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/Button"
import { useAuth } from "@/utils/auth"
import { getProductName } from "@/utils/product-name"
import { Sentry } from "@/telemetry/sentry"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import logoPixiesetWhiteUrl from "@assets/logo_pixieset_white.png?url"
import productEditorUrl from "@assets/icons/product-editor.png?url"
import { pixiesetURLs } from "@/utils/pixieset-urls"
import { isElectronApp, notifyAppReady } from "@/utils/electron"

export const AuthPage = () => {
	const { t } = useTranslation("components")
	const { loginWithRedirect, isLoading } = useAuth()
	const productName = getProductName()
	const isPixieset = isPixiesetAuthEnabled()

	useLayoutEffect(() => {
		notifyAppReady(APP_VERSION)
	}, [])

	const returnTo = window.location.pathname + window.location.search + window.location.hash

	const handleLogin = () => {
		loginWithRedirect({
			authorizationParams: isElectronApp ? { prompt: "login" } : undefined,
			appState: {
				returnTo,
			},
		}).catch(Sentry.captureException)
	}

	const handleSignUp = () => {
		if (isPixieset) {
			window.open(pixiesetURLs.accounts.registration, "_blank", "noopener")
			return
		}
		loginWithRedirect({
			authorizationParams: {
				...(isElectronApp ? { prompt: "login" } : {}),
				screen_hint: "signup",
			},
			appState: {
				returnTo,
			},
		}).catch(Sentry.captureException)
	}

	return (
		<div className="electron-drag relative flex size-full items-center justify-center overflow-hidden bg-base-900 text-base-50">
			<img
				className="pointer-events-none absolute left-7 top-7 h-[20px] w-auto invert dark:invert-0"
				src={logoPixiesetWhiteUrl}
				alt={t("authPage.altPixieset")}
			/>
			<div className="relative flex flex-col items-center space-y-4 text-center">
				<img className="size-10" src={productEditorUrl} alt="" />
				<h1 className="text-lg font-semibold text-base-100">
					{t("authPage.logInTo", { productName })}
				</h1>
				<Button
					trackingLabel="auth_login"
					aria-label={t("authPage.ariaLogIn")}
					variant="primary"
					size="default"
					onClick={handleLogin}
					disabled={isLoading}
				>
					{isElectronApp ? t("authPage.openBrowserToLogIn") : t("authPage.logIn")}
				</Button>
				<div className="flex items-center gap-1 text-xs text-base-400">
					<span>{t("authPage.noAccount")}</span>
					<Button
						trackingLabel="auth_signup"
						aria-label={t("authPage.ariaSignUp")}
						variant="custom"
						size="custom"
						className="h-auto px-0 text-xs text-accent-500 hover:text-accent-400"
						onClick={handleSignUp}
						disabled={isLoading}
					>
						{t("authPage.signUp")}
					</Button>
				</div>
			</div>
		</div>
	)
}
